/**
 * RealTimeSimulation.jsx
 * 
 * Component for managing historical data replay simulations.
 * Allows users to create, configure, and control simulations that replay
 * OHLC data from TimescaleDB to Kafka as simulated real-time ticks.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  FiPlay, FiSquare, FiTrash2, FiPlus, FiRefreshCw, 
  FiClock, FiCalendar, FiDatabase, FiActivity, FiSearch, FiX, FiCheck, FiPause
} from 'react-icons/fi';
import { services } from '../../../../services/api';
import WebSocketManager from '../../../../websocket/WebSocketManager';
import useBreakpoint from '../../../../hooks/useBreakpoint';

const RealTimeSimulation = ({ prefillData, onPrefillConsumed }) => {
  //! Phone gets CARDS, not the table. The simulations table is 7 columns wide
  //! (~900px) and only "fits" a 375px screen by scrolling sideways, which reads
  //! as broken/cut-off — and the Play/Pause/Stop/Delete controls live in the LAST
  //! column, so the actions were the first thing off-screen. Desktop keeps the table.
  const { isPhone } = useBreakpoint();

  // --> State
  const [simulations, setSimulations] = useState([]);
  //! loading = first-mount spinner (replaces whole table). refreshing = silent background
  //! refetch — keeps rows visible so in-progress bars don't blink out on every action.
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // Progress tracking for running simulations
  const [progressMap, setProgressMap] = useState({});
  const wsSubscriptions = useRef(new Set());
  
  // Instrument selection state
  const [showForm, setShowForm] = useState(false);
  const [instruments, setInstruments] = useState([]);
  const [instrumentsLoading, setInstrumentsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  
  // Form state for configuration
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    timeframe: '1m',
    delay_ms: 1000,
    //-- Continuous-contract metadata (F&O only; legacy payloads send false/null)
    is_continuous: false,
    rollover_days: null,
  });
  const [submitting, setSubmitting] = useState(false);

  // --> Effects
  useEffect(() => {
    fetchSimulations(true);
  }, []);

  // Fetch available instruments when form opens
  useEffect(() => {
    if (showForm && instruments.length === 0) {
      fetchInstruments();
    }
  }, [showForm]);

  // WebSocket subscription for progress updates
  const handleProgressUpdate = useCallback((data) => {
    console.log('📊 Simulation progress update:', data);
    if (data.config_id) {
      setProgressMap(prev => ({
        ...prev,
        [data.config_id]: {
          percent: data.percent_complete || 0,
          ticksSent: data.ticks_sent || 0,
          totalTicks: data.total_ticks || 0,
          status: data.status,
        }
      }));
      // If completed, refresh the list
      if (data.status === 'completed' || data.status === 'stopped') {
        fetchSimulations();
      }
    }
  }, []);

  // Subscribe to progress updates for running simulations
  useEffect(() => {
    const runningSimIds = simulations
      .filter(s => s.status === 'running')
      .map(s => s.id);
    
    // Subscribe to new running simulations
    runningSimIds.forEach(id => {
      if (!wsSubscriptions.current.has(id)) {
        const topic = `simulation.${id}.progress`;
        console.log('🔌 Subscribing to simulation progress:', topic);
        WebSocketManager.subscribeTopic(topic, handleProgressUpdate);
        wsSubscriptions.current.add(id);
      }
    });

    // Unsubscribe from stopped simulations
    wsSubscriptions.current.forEach(id => {
      if (!runningSimIds.includes(id)) {
        const topic = `simulation.${id}.progress`;
        console.log('🔌 Unsubscribing from simulation progress:', topic);
        WebSocketManager.unsubscribeTopic(topic, handleProgressUpdate);
        wsSubscriptions.current.delete(id);
      }
    });

    // Cleanup on unmount
    return () => {
      wsSubscriptions.current.forEach(id => {
        WebSocketManager.unsubscribeTopic(`simulation.${id}.progress`, handleProgressUpdate);
      });
      wsSubscriptions.current.clear();
    };
  }, [simulations, handleProgressUpdate]);

  // Handle prefill data from timeline view
  useEffect(() => {
    if (prefillData) {
      console.log('📋 Prefilling simulation form:', prefillData);
      
      // Convert YYYY-MM-DD to YYYY-MM-DDTHH:mm format for datetime-local input
      // Default to market hours: 09:15 start, 15:30 end (IST trading hours)
      const formatDateForInput = (dateStr, isEnd = false) => {
        if (!dateStr) return '';
        // If already has time component, extract just the date part
        const datePart = dateStr.slice(0, 10);
        // Append default trading hours
        return isEnd ? `${datePart}T15:30` : `${datePart}T09:15`;
      };
      
      // Create a pseudo-instrument object from prefillData
      const pseudoInstrument = {
        instrument_id: prefillData.instrument.instrument_id,
        symbol: prefillData.instrument.symbol,
        exchange: prefillData.instrument.exchange,
        //-- Carry instrument_type through so the continuous-contract UI can gate on FUTURE
        instrument_type: prefillData.instrument.instrument_type || prefillData.instrument.type || null,
        total_records: prefillData.instrument.total_records,
        first_data_time: prefillData.start_date,
        last_data_time: prefillData.end_date,
      };
      setSelectedInstrument(pseudoInstrument);
      setFormData({
        name: `${prefillData.instrument.symbol} ${prefillData.timeframe} Replay`,
        start_date: formatDateForInput(prefillData.start_date, false),
        end_date: formatDateForInput(prefillData.end_date, true),
        timeframe: prefillData.timeframe,
        delay_ms: 1000,
        //-- Continuous-contract metadata forwarded from HistoricalDataTimeline (Task 5)
        is_continuous: !!prefillData.is_continuous,
        rollover_days: prefillData.rollover_days ?? null,
      });
      setShowForm(true);
      // Consume the prefill so it doesn't re-trigger
      if (onPrefillConsumed) onPrefillConsumed();
    }
  }, [prefillData, onPrefillConsumed]);

  // --> API Handlers
  //--> isInitial=true on first mount only; subsequent calls use `refreshing` so the
  //* table body is not replaced with a full "Loading simulations..." row (the progress
  //* column would otherwise blink on every start/pause/stop action).
  const fetchSimulations = async (isInitial = false) => {
    if (isInitial) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const response = await services.simulation.list();
      setSimulations(response.data.simulations || []);
    } catch (err) {
      console.error('Failed to fetch simulations:', err);
      setError('Failed to load simulations');
    } finally {
      if (isInitial) setLoading(false);
      else setRefreshing(false);
    }
  };

  const fetchInstruments = async () => {
    setInstrumentsLoading(true);
    try {
      const response = await services.marketData.listInstruments({ limit: 500 });
      setInstruments(response.data.instruments || []);
    } catch (err) {
      console.error('Failed to fetch instruments:', err);
      setError('Failed to load instruments from TimescaleDB');
    } finally {
      setInstrumentsLoading(false);
    }
  };

  // Filter instruments based on search
  const filteredInstruments = useMemo(() => {
    if (!searchQuery.trim()) return instruments;
    const query = searchQuery.toLowerCase();
    return instruments.filter(inst => 
      inst.symbol?.toLowerCase().includes(query) ||
      inst.exchange?.toLowerCase().includes(query) ||
      inst.instrument_id?.toLowerCase().includes(query)
    );
  }, [instruments, searchQuery]);

  const handleSelectInstrument = (instrument) => {
    setSelectedInstrument(instrument);
    setFormData({
      ...formData,
      name: `${instrument.symbol} ${formData.timeframe} Replay`,
      // Set date range from available data
      start_date: instrument.first_data_time?.slice(0, 16) || '',
      end_date: instrument.last_data_time?.slice(0, 16) || '',
    });
    setSearchQuery('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!selectedInstrument) {
      setError('Please select an instrument');
      return;
    }
    
    setSubmitting(true);
    try {
      //-- Derive continuous-contract base_symbol from the fully-qualified futures symbol
      //* e.g. "CRUDEOIL25OCTFUT" -> "CRUDEOIL" (strip trailing YYDDDMMM... suffix)
      const deriveBaseSymbol = (fullSymbol) => {
        return fullSymbol ? fullSymbol.replace(/\d{2}[A-Z]{3}.*$/, '') : fullSymbol;
      };

      const base_symbol = formData.is_continuous
        ? deriveBaseSymbol(selectedInstrument.symbol)
        : null;
      const instrument_type = selectedInstrument.instrument_type || 'FUTURE';

      // -->  Pre-create alignment guardrail (continuous sims only; warn-only)
      if (formData.is_continuous) {
        try {
          const alignment = await services.simulation.checkAlignment({
            base_symbol,
            exchange: selectedInstrument.exchange,
            instrument_type,
            rollover_days: formData.rollover_days ?? null,
          });
          if (!alignment.aligned && alignment.mismatches.length > 0) {
            const summary = alignment.mismatches
              .map(m => `• ${m.source === 'strategy_deployment' ? 'Strategy' : 'Watchlist'} "${m.name}" uses rollover_days=${m.rollover_days}`)
              .join('\n');
            const proceed = window.confirm(
              `⚠️ Rollover mismatch — this sim will NOT feed the following consumers:\n\n${summary}\n\nThe sim's UUIDs won't intersect with their subscriptions.\n\nCreate anyway?`
            );
            if (!proceed) return;
          }
        } catch (e) {
          console.warn('[DIAG] alignment check failed, proceeding without warning', e);
        }
      }

      await services.simulation.create({
        name: formData.name,
        instrument_id: selectedInstrument.instrument_id,
        symbol: selectedInstrument.symbol,
        exchange: selectedInstrument.exchange,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        timeframe: formData.timeframe,
        delay_ms: parseInt(formData.delay_ms, 10),
        //-- Continuous-contract metadata (legacy payloads send false/null/null/INSTRUMENT_TYPE)
        is_continuous: !!formData.is_continuous,
        rollover_days: formData.is_continuous ? (formData.rollover_days ?? null) : null,
        base_symbol,
        instrument_type,
      });

      // Reset form
      setShowForm(false);
      setSelectedInstrument(null);
      setFormData({
        name: '', start_date: '', end_date: '', timeframe: '1m', delay_ms: 1000,
        is_continuous: false, rollover_days: null,
      });
      fetchSimulations();
    } catch (err) {
      console.error('Failed to create simulation:', err);
      setError('Failed to create simulation');
    } finally {
      setSubmitting(false);
    }
  };

  //--> Track which simulations have a pending action (prevents double-click)
  const [pendingAction, setPendingAction] = useState({});

  const handleStart = async (id) => {
    if (pendingAction[id]) return;
    setPendingAction(prev => ({ ...prev, [id]: 'starting' }));
    //--> Optimistic: immediately show as running in the list
    setSimulations(prev => prev.map(s => s.id === id ? { ...s, status: 'running' } : s));
    try {
      await services.simulation.start(id);
      //! Do NOT refetch on success — WS progress updates drive the row from here. A
      //! refetch would bounce the table into a 'refreshing' state before the first
      //! progress event arrives, which is what the user noticed as "entire table loading".
    } catch (err) {
      console.error('Failed to start simulation:', err);
      setError('Failed to start simulation');
      fetchSimulations(); // revert optimistic update
    } finally {
      setPendingAction(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  };

  const handleStop = async (id) => {
    if (pendingAction[id]) return;
    setPendingAction(prev => ({ ...prev, [id]: 'stopping' }));
    setSimulations(prev => prev.map(s => s.id === id ? { ...s, status: 'stopped' } : s));
    try {
      await services.simulation.stop(id);
      fetchSimulations();
    } catch (err) {
      console.error('Failed to stop simulation:', err);
      setError('Failed to stop simulation');
      fetchSimulations();
    } finally {
      setPendingAction(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  };

  const handlePause = async (id) => {
    if (pendingAction[id]) return;
    setPendingAction(prev => ({ ...prev, [id]: 'pausing' }));
    setSimulations(prev => prev.map(s => s.id === id ? { ...s, status: 'paused' } : s));
    try {
      await services.simulation.pause(id);
      fetchSimulations();
    } catch (err) {
      console.error('Failed to pause simulation:', err);
      setError('Failed to pause simulation');
      fetchSimulations();
    } finally {
      setPendingAction(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this simulation?')) return;
    try {
      await services.simulation.delete(id);
      fetchSimulations();
    } catch (err) {
      console.error('Failed to delete simulation:', err);
      setError('Failed to delete simulation');
    }
  };

  // --> Status Badge Component
  const StatusBadge = ({ status }) => {
    const variants = {
      created: 'neutral',
      running: 'profit',
      stopped: 'warn',
      completed: 'info',
    };
    return (
      <span className={`ds-badge sm ${variants[status] || variants.created} ${status === 'running' ? 'animate-pulse' : ''}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  //--> Shared by both presentations so the two can't drift.
  const delayLabel = (sim) => (sim.delay_ms >= 1000 ? `${sim.delay_ms / 1000}s` : `${sim.delay_ms}ms`);

  const listLoadingState = (
    <div className="px-4 py-8 text-center text-fg-2">
      <FiRefreshCw className="inline animate-spin mr-2" /> Loading simulations...
    </div>
  );

  const listEmptyState = (
    <div className="px-4 py-8 text-center text-fg-2">
      <FiActivity className="inline mr-2" /> No simulations yet. Create one to get started.
    </div>
  );

  //--> PHONE: one card per simulation. The progress bar gets the full width
  //    instead of a 128px column, and Play/Pause/Stop/Delete become labelled DS
  //    buttons — 44px on a coarse pointer, versus four 34px `p-2` icon stubs
  //    crammed into the table's last (i.e. first off-screen) column.
  const renderSimulationCards = () => (
    <div className="flex flex-col gap-3">
      {simulations.map((sim) => (
        <div key={sim.id} className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-fg-1">{sim.name}</div>
              <div className="mt-1 text-xs text-fg-2">
                <span className="font-mono">{sim.symbol}</span>
                <span className="text-fg-3 mx-1">•</span>
                <span>{sim.exchange}</span>
              </div>
            </div>
            <div className="shrink-0">
              <StatusBadge status={sim.status} />
            </div>
          </div>

          {(sim.status === 'running' || progressMap[sim.id]) && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-fg-2">
                  {progressMap[sim.id]?.ticksSent?.toLocaleString() || 0} / {progressMap[sim.id]?.totalTicks?.toLocaleString() || '?'}
                </span>
                <span className="font-semibold text-profit">
                  {(progressMap[sim.id]?.percent || 0).toFixed(1)}%
                </span>
              </div>
              <div className="h-2 bg-bg-3 rounded-full overflow-hidden">
                <div
                  className="h-full bg-profit transition-all duration-300"
                  style={{ width: `${progressMap[sim.id]?.percent || 0}%` }}
                />
              </div>
            </div>
          )}

          {sim.status === 'completed' && !progressMap[sim.id] && (
            <div className="mt-3 text-xs text-accent">100% Complete</div>
          )}

          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-[var(--divider)] pt-3">
            <div>
              <dt className="text-xs font-semibold uppercase text-fg-3">Date Range</dt>
              <dd className="mt-0.5 text-xs text-fg-2">
                {sim.start_date?.slice(0, 10)} → {sim.end_date?.slice(0, 10)}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-fg-3">Delay</dt>
              <dd className="mt-0.5 text-xs text-fg-2">{delayLabel(sim)}</dd>
            </div>
          </dl>

          <div className="mt-3 flex gap-2 border-t border-[var(--divider)] pt-3">
            {sim.status === 'running' ? (
              <>
                <button
                  type="button"
                  onClick={() => handlePause(sim.id)}
                  disabled={!!pendingAction[sim.id]}
                  className="btn btn-secondary flex-1"
                >
                  <FiPause /> Pause
                </button>
                <button
                  type="button"
                  onClick={() => handleStop(sim.id)}
                  disabled={!!pendingAction[sim.id]}
                  className="btn btn-danger flex-1"
                >
                  <FiSquare /> Stop
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleStart(sim.id)}
                  disabled={!!pendingAction[sim.id]}
                  className="btn btn-primary flex-1"
                >
                  {pendingAction[sim.id] === 'starting' ? <FiRefreshCw className="animate-spin" /> : <FiPlay />}
                  {pendingAction[sim.id] === 'starting' ? 'Starting...' : sim.status === 'paused' ? 'Resume' : 'Start'}
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(sim.id)}
                  className="btn btn-danger btn-icon shrink-0"
                  title="Delete"
                  aria-label="Delete simulation"
                >
                  <FiTrash2 />
                </button>
              </>
            )}
          </div>
        </div>
      ))}

      {loading && (
        <div className="rounded-xl border border-[var(--divider)] bg-bg-2">{listLoadingState}</div>
      )}
      {!loading && simulations.length === 0 && (
        <div className="rounded-xl border border-[var(--divider)] bg-bg-2">{listEmptyState}</div>
      )}
    </div>
  );

  const renderSimulationTable = () => (
    <div className="bg-bg-2 rounded-xl border border-[var(--divider)] shadow-sm overflow-x-auto">
      <table className="w-full">
        <thead className="bg-bg-3 dark:bg-bg-3/50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase">Name</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase">Instrument</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase">Date Range</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase">Delay</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase">Progress</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase">Status</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-fg-2 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--divider)]">
          {loading ? (
            <tr>
              <td colSpan="7">{listLoadingState}</td>
            </tr>
          ) : simulations.length === 0 ? (
            <tr>
              <td colSpan="7">{listEmptyState}</td>
            </tr>
          ) : (
            simulations.map((sim) => (
              <tr key={sim.id} className="hover:bg-bg-3/30 transition-colors">
                <td className="px-4 py-3 text-sm font-medium text-fg-1">
                  {sim.name}
                </td>
                <td className="px-4 py-3 text-sm text-fg-2">
                  <span className="font-mono text-xs">{sim.symbol}</span>
                  <span className="text-fg-3 mx-1">•</span>
                  <span className="text-xs text-fg-2">{sim.exchange}</span>
                </td>
                <td className="px-4 py-3 text-xs text-fg-2">
                  {sim.start_date?.slice(0, 10)} → {sim.end_date?.slice(0, 10)}
                </td>
                <td className="px-4 py-3 text-sm text-fg-2">
                  {delayLabel(sim)}
                </td>
                <td className="px-4 py-3">
                  {/* Progress Bar */}
                  {sim.status === 'running' || progressMap[sim.id] ? (
                    <div className="w-32">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-fg-2">
                          {progressMap[sim.id]?.ticksSent?.toLocaleString() || 0} / {progressMap[sim.id]?.totalTicks?.toLocaleString() || '?'}
                        </span>
                        <span className="font-semibold text-profit">
                          {(progressMap[sim.id]?.percent || 0).toFixed(1)}%
                        </span>
                      </div>
                      <div className="h-2 bg-bg-3 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-profit transition-all duration-300"
                          style={{ width: `${progressMap[sim.id]?.percent || 0}%` }}
                        />
                      </div>
                    </div>
                  ) : sim.status === 'completed' ? (
                    <span className="text-xs text-accent">100% Complete</span>
                  ) : (
                    <span className="text-xs text-fg-3">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={sim.status} />
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {sim.status === 'running' ? (
                      <>
                        <button
                          onClick={() => handlePause(sim.id)}
                          disabled={!!pendingAction[sim.id]}
                          className="p-2 text-warning hover:bg-[var(--badge-warn-bg)] rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Pause"
                        >
                          <FiPause />
                        </button>
                        <button
                          onClick={() => handleStop(sim.id)}
                          disabled={!!pendingAction[sim.id]}
                          className="p-2 text-loss hover:bg-loss/10 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Stop (Reset)"
                        >
                          <FiSquare />
                        </button>
                      </>
                    ) : (
                      <>
                        {/* Resume/Start Button */}
                        <button
                          onClick={() => handleStart(sim.id)}
                          disabled={!!pendingAction[sim.id]}
                          className="p-2 text-profit hover:bg-profit/10 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                          title={pendingAction[sim.id] === 'starting' ? "Starting..." : sim.status === 'paused' ? "Resume" : "Start"}
                        >
                          {pendingAction[sim.id] === 'starting' ? <FiRefreshCw className="animate-spin" /> : <FiPlay />}
                        </button>

                        {/* Allow delete if not running */}
                        <button
                          onClick={() => handleDelete(sim.id)}
                          className="p-2 text-fg-3 hover:text-loss hover:bg-loss/10 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  // --> Render
  return (
    <div className="space-y-6">
      {/* Header — stacks below sm: the title's 52-char description and the two
          buttons collided at 375px with no wrap to escape into. */}
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="ds-h2 text-fg-1">
            Real-time Simulation
          </h2>
          <p className="text-sm text-fg-2">
            Replay historical data as simulated live ticks for testing
          </p>
        </div>
        <div className="flex shrink-0 gap-3">
          <button
            //! Wrap the call: React passes the SyntheticEvent as arg 0, which would
            //! make isInitial truthy and flash the whole table body on manual refresh.
            onClick={() => fetchSimulations()}
            className="btn btn-ghost btn-icon"
            title="Refresh"
          >
            <FiRefreshCw className={(loading || refreshing) ? 'animate-spin' : ''} />
          </button>
          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="btn btn-primary flex items-center gap-2"
          >
            <FiPlus /> New Simulation
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-loss/10 border border-loss/30 rounded-lg text-loss text-sm
                        flex justify-between items-center gap-3">
          <span>{error}</span>
          {/* -m-2 p-3: was p-1 around a 16px glyph (~24px total). 16 + 24 = a 44px
              hit area, negative-margined so the banner height doesn't grow. */}
          <button
            onClick={() => setError(null)}
            className="-m-2 shrink-0 rounded-lg p-3 hover:bg-loss/15 transition-colors"
            title="Dismiss"
          >
            <FiX />
          </button>
        </div>
      )}

      {/* Create Form - Instrument Selection */}
      {showForm && (
        <div className="p-6 bg-bg-2 rounded-xl border border-[var(--divider)] shadow-sm">
          <h3 className="ds-h3 text-fg-1 mb-4">
            Create New Simulation
          </h3>
          
          {/* Step 1: Select Instrument */}
          {!selectedInstrument ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-fg-2">
                <FiDatabase /> <span className="font-medium">Step 1:</span> Select instrument from TimescaleDB
              </div>
              
              {/* Search */}
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-3" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search instruments..."
                  className="ds-input w-full pl-10"
                />
              </div>
              
              {/* Instrument List — phone renders plain tappable rows instead of the
                  5-column table: the row itself is already the whole hit target, so
                  the table's trailing "Select" button was a second control for the
                  same action and the columns only fit by scrolling sideways. */}
              <div className="max-h-[400px] overflow-y-auto border border-[var(--divider)] rounded-lg">
                {instrumentsLoading ? (
                  <div className="p-8 text-center text-fg-2">
                    <FiRefreshCw className="inline animate-spin mr-2" /> Loading instruments...
                  </div>
                ) : filteredInstruments.length === 0 ? (
                  <div className="p-8 text-center text-fg-2">
                    No instruments found {searchQuery && `matching "${searchQuery}"`}
                  </div>
                ) : isPhone ? (
                  <div className="divide-y divide-[var(--divider)]">
                    {filteredInstruments.map((inst) => (
                      <button
                        key={inst.instrument_id}
                        type="button"
                        onClick={() => handleSelectInstrument(inst)}
                        className="block w-full px-4 py-3 text-left transition-colors hover:bg-accent/10 active:bg-accent/15"
                      >
                        <div className="flex items-center gap-2">
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg-1">
                            {inst.symbol}
                          </span>
                          <span className="shrink-0 rounded bg-bg-3 px-2 py-0.5 text-xs text-fg-2">
                            {inst.exchange}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-fg-2">
                          {inst.first_data_time?.slice(0, 10)} → {inst.last_data_time?.slice(0, 10)}
                        </div>
                        <div className="mt-0.5 text-xs text-fg-3">
                          {inst.total_records?.toLocaleString() || '-'} records
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-bg-3 dark:bg-bg-3/50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-fg-2 uppercase">Symbol</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-fg-2 uppercase">Exchange</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-fg-2 uppercase">Data Range</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-fg-2 uppercase">Records</th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-fg-2 uppercase">Select</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--divider)]">
                      {filteredInstruments.map((inst) => (
                        <tr
                          key={inst.instrument_id}
                          className="hover:bg-accent/10 cursor-pointer transition-colors"
                          onClick={() => handleSelectInstrument(inst)}
                        >
                          <td className="px-4 py-3 text-sm font-medium text-fg-1">
                            {inst.symbol}
                          </td>
                          <td className="px-4 py-3 text-sm text-fg-2">
                            <span className="px-2 py-0.5 bg-bg-3 rounded text-xs">
                              {inst.exchange}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-fg-2">
                            {inst.first_data_time?.slice(0, 10)} → {inst.last_data_time?.slice(0, 10)}
                          </td>
                          <td className="px-4 py-3 text-sm text-fg-2">
                            {inst.total_records?.toLocaleString() || '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              className="btn btn-ghost btn-icon"
                              onClick={(e) => { e.stopPropagation(); handleSelectInstrument(inst); }}
                              title="Select"
                            >
                              <FiCheck />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* Step 2: Configure Simulation */
            <form onSubmit={handleCreate} className="space-y-6">
              <div className="flex items-center gap-2 text-sm text-fg-2">
                <FiClock /> <span className="font-medium">Step 2:</span> Configure simulation settings
              </div>
              
              {/* Selected Instrument Card */}
              <div className="p-4 bg-accent/10 border border-accent/25 rounded-lg">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <span className="font-semibold text-fg-1">{selectedInstrument.symbol}</span>
                    <span className="ml-2 px-2 py-0.5 bg-accent/15 rounded text-xs text-accent">
                      {selectedInstrument.exchange}
                    </span>
                    <p className="text-xs text-fg-2 mt-1">
                      Data available: {selectedInstrument.first_data_time?.slice(0, 10)} → {selectedInstrument.last_data_time?.slice(0, 10)}
                      <span className="ml-2">({selectedInstrument.total_records?.toLocaleString()} records)</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedInstrument(null)}
                    className="-m-2 shrink-0 rounded-lg p-3 text-fg-2 hover:bg-accent/15 hover:text-fg-1 transition-colors"
                    title="Change instrument"
                  >
                    <FiX />
                  </button>
                </div>
              </div>
              
              {/* Configuration Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-fg-2 mb-1">
                    Simulation Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="ds-input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-fg-2 mb-1">
                    <FiCalendar className="inline mr-1" /> Start Date
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="ds-input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-fg-2 mb-1">
                    <FiCalendar className="inline mr-1" /> End Date
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="ds-input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-fg-2 mb-1">
                    <FiDatabase className="inline mr-1" /> Timeframe
                  </label>
                  <select
                    value={formData.timeframe}
                    onChange={(e) => {
                      const tf = e.target.value;
                      setFormData({ 
                        ...formData, 
                        timeframe: tf,
                        name: `${selectedInstrument.symbol} ${tf} Replay`
                      });
                    }}
                    className="ds-input w-full"
                    required
                  >
                    <option value="1s">1 Second</option>
                    <option value="1m">1 Minute</option>
                    <option value="5m">5 Minutes</option>
                    <option value="15m">15 Minutes</option>
                    <option value="1h">1 Hour</option>
                  </select>
                </div>
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-fg-2 mb-1">
                    <FiClock className="inline mr-1" /> Delay Between Ticks
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    <input
                      type="number"
                      value={formData.delay_ms}
                      onChange={(e) => setFormData({ ...formData, delay_ms: e.target.value })}
                      className="ds-input w-24"
                      min="10"
                      max="60000"
                      required
                    />
                    <span className="flex items-center text-sm text-fg-2">ms</span>
                    <div className="flex gap-1 ml-2">
                      {[100, 500, 1000, 2000, 5000].map((ms) => (
                        <button
                          key={ms}
                          type="button"
                          onClick={() => setFormData({ ...formData, delay_ms: ms })}
                          //-- Unselected hover was `hover:bg-bg-3` on a `bg-bg-3` chip
                          //-- (and the dark: variant repeated it) — a no-op in both
                          //-- themes. An accent tint gives it actual feedback.
                          className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                            parseInt(formData.delay_ms) === ms
                              ? 'bg-accent text-white'
                              : 'bg-bg-3 text-fg-2 hover:bg-accent/15 hover:text-fg-1'
                          }`}
                        >
                          {ms >= 1000 ? `${ms/1000}s` : `${ms}ms`}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Continuous Contract Section — F&O (FUTURE) instruments only */}
              {(selectedInstrument?.instrument_type || selectedInstrument?.type || '').toUpperCase() === 'FUTURE' && (
                <div className="bg-accent/10 rounded-lg p-4 border border-accent/25">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_continuous}
                      onChange={(e) => setFormData({ ...formData, is_continuous: e.target.checked })}
                      className="mt-1 w-5 h-5 rounded accent-accent"
                    />
                    <div>
                      <span className="font-semibold text-fg-1">Continuous Contract Mode</span>
                      <p className="text-sm text-fg-2 mt-1">
                        Replay stitched chain data and publish each bar on the active contract's UUID per rollover rules.
                      </p>
                    </div>
                  </label>
                  {formData.is_continuous && (
                    <div className="mt-4 pl-8">
                      <label className="block text-sm font-medium text-fg-2 mb-2">
                        Rollover Days Before Expiry (blank = exchange default)
                      </label>
                      <select
                        value={formData.rollover_days ?? ''}
                        onChange={(e) => setFormData({ ...formData, rollover_days: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                        className="ds-input w-full"
                      >
                        <option value="">Exchange default</option>
                        {[0, 1, 2, 3, 4, 5].map(d => (
                          <option key={d} value={d}>{d === 0 ? 'No rollover' : `${d} day${d > 1 ? 's' : ''} before expiry`}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-[var(--divider)]">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setSelectedInstrument(null); }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary disabled:opacity-50"
                >
                  {submitting ? 'Creating...' : 'Create Simulation'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Simulations list — cards on a phone, table on desktop. */}
      {isPhone ? renderSimulationCards() : renderSimulationTable()}

      {/* Info Box */}
      <div className="p-4 bg-accent/10 border border-accent/25 rounded-lg text-fg-2 text-sm">
        <strong className="text-fg-1">Note:</strong> Simulated ticks are published to Kafka with <code className="bg-accent/15 text-accent px-1 rounded">tick_type: "simulated"</code>.
        The OHLC Aggregator will process them for testing. Trade services will restrict to paper trading only.
      </div>
    </div>
  );
};

export default RealTimeSimulation;
